"use client";

import {JSX, MouseEvent, ReactNode, useEffect} from "react";

import {type LocationOption, type LocationSearchInstance} from "@/features/locations";

import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";
import {useServerRouter} from "@/shared/hooks/navigation/use-server-router";
import {useModelSnapshot} from "@/shared/hooks/state-management/use-model-snapshot";
import {useTranslation} from "@/shared/i18n";
import {Spinner} from "@/shared/ui/feedback/spinner";
import {TextField} from "@/shared/ui/form/fields";

import {LocationSection} from "./parts";

import "./styles/location-search-view.scss";

interface LocationSearchViewProps {
    model: LocationSearchInstance;
    label?: string;
    className?: string;
    placeholder?: string;
    hidePlaceholderOnFocus?: boolean;
    icon?: ReactNode;
    iconPosition?: "left" | "right";
    dropdownOffset?: number; // Offset from input in pixels (default: 10)
    /** Called when dropdown closes (click outside) or field loses focus — parent can collapse / hide label */
    onClose?: () => void;
}

interface ActionProps {
    isShow: boolean;
    onClick(): void;
    onMouseDown(e: MouseEvent): void;
    ariaLabel: string;
}
function ActionClose({isShow, onClick, onMouseDown, ariaLabel}: ActionProps): JSX.Element | null {
    if (!isShow) return null;

    return (
        <button type="button" onMouseDown={onMouseDown} onClick={onClick} className="location-search__clear" aria-label={ariaLabel}>
            ×
        </button>
    );
}
export function LocationSearchView({
    model,
    label,
    icon,
    iconPosition,
    className,
    placeholder = "",
    hidePlaceholderOnFocus,
    dropdownOffset = 10,
    onClose,
}: LocationSearchViewProps): JSX.Element {
    const snapshot = useModelSnapshot(model);
    useServerRouter(model);
    const {t} = useTranslation("search");

    const handleClose = (): void => {
        if (snapshot.isOpen) {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            model.close();
        }
        onClose?.();
    };

    // Close dropdown when clicking outside (with smart tap/scroll detection)
    const dropdownRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: handleClose,
        enableSmartTouch: true, // Distinguish tap from scroll (for input with keyboard)
    });

    // Notify parent when input loses focus (e.g. tab out) so label can be hidden; close dropdown
    useEffect(() => {
        return model.input.events.on("blur", () => {
            if (model._suppressNextBlur) {
                model._suppressNextBlur = false;
                return;
            }
            model.close();
            onClose?.();
        });
    }, [model.input.events, model, onClose]);

    const handleOptionClick = (option: LocationOption): void => {
        // Blur active element (input) to prevent keyboard jump on mobile
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        model.selectOption(option);
    };

    const handleClearMouseDown = (): void => {
        model._suppressNextBlur = true;
    };

    const handleClear = (): void => {
        model.clear();
    };

    return (
        <div ref={dropdownRef} className={`location-search ${className || ""}`}>
            <TextField
                model={model.input}
                id="location-search-input"
                label={label}
                autoComplete="off"
                type="text"
                autoCorrect="off"
                autoCapitalize="off"
                placeholder={placeholder}
                hidePlaceholderOnFocus={hidePlaceholderOnFocus}
                icon={icon}
                iconPosition={iconPosition}
                className={`location-search__field ${snapshot.hasError ? "location-search__field--error" : ""}`}
                action={
                    snapshot.shouldShowLoading ? (
                        <Spinner size="small" />
                    ) : (
                        <ActionClose
                            isShow={!!snapshot.input.value}
                            onClick={handleClear}
                            onMouseDown={handleClearMouseDown}
                            ariaLabel={t("$.common.clear")}
                        />
                    )
                }
            />

            {snapshot.isOpen && (snapshot.hasResults || snapshot.hasEmptyResults) && (
                <div
                    className="location-search__dropdown"
                    style={{
                        // @ts-expect-error - CSS custom property
                        "--dropdown-offset": `${dropdownOffset}px`,
                    }}
                >
                    {snapshot.hasResults && (
                        <>
                            <LocationSection title={t("countries")} options={snapshot.filteredOptions.countries} onSelect={handleOptionClick} />
                            <LocationSection title={t("states")} options={snapshot.filteredOptions.states} onSelect={handleOptionClick} />
                            <LocationSection title={t("destinations")} options={snapshot.filteredOptions.destinations} onSelect={handleOptionClick} />
                            <LocationSection title={t("cities")} options={snapshot.filteredOptions.cities} onSelect={handleOptionClick} />
                            <LocationSection title={t("rentals")} options={snapshot.filteredOptions.rentals} onSelect={handleOptionClick} />
                        </>
                    )}

                    {snapshot.hasEmptyResults && <div className="location-search__empty">{t("noResultsFound")}</div>}
                </div>
            )}

            {snapshot.hasError && (
                <div className="location-search__error">
                    <span className="location-search__error-arrow" />
                    {t(`$.${snapshot.error}` as Parameters<typeof t>[0])}
                </div>
            )}
        </div>
    );
}
